import * as React from 'react';
import {
  Col,
  Input,
  Row,
  Label,
  Form,
  FormInstance,
  Icon,
  Tooltip,
  Switch,
  Space,
} from 'ebs-design';
import { SpaceProps } from 'ebs-design/dist/components/atoms/Space/Space';
import { useWatch } from 'hooks/useWatch';

type Props = {
  label?: string;
  fieldPrefix?: string;
  form?: FormInstance;
  disableMinutes?: boolean;
  required?: boolean;
  helpText?: string;
  hasToggle?: boolean;
} & Omit<SpaceProps, 'form'>;

export const DurationFields: React.FC<Props> = ({
  label,
  fieldPrefix,
  form,
  disableMinutes,
  required = false,
  helpText,
  hasToggle = false,
  ...props
}) => {
  const validateDuration = (value: any): Promise<void> => {
    if (!form) return Promise.resolve();

    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (!Number.isInteger(numValue)) {
        return Promise.reject(new Error('Duration must be a whole number'));
      }
    }

    const days = Number(form.getFieldValue(`${fieldPrefix}_duration_days`)) || 0;
    const hours = Number(form.getFieldValue(`${fieldPrefix}_duration_hours`)) || 0;
    const minutes = Number(form.getFieldValue(`${fieldPrefix}_duration_minutes`)) || 0;

    if (days === 0 && hours === 0 && minutes === 0) {
      return Promise.reject(new Error('At least one duration field must be greater than 0'));
    }
    return Promise.resolve();
  };

  const isActiveField = useWatch(form as FormInstance, `${fieldPrefix}_is_active`);

  return (
    <Space direction="vertical" align="start" size={0} {...props}>
      {label && <Label text={label} />}

      <Space align="center" size="small">
        <Row gx={2} className="mb-15" style={{ width: '100%' }}>
          <Col size={helpText ? 6 : 4}>
            <Form.Field
              labelOptions={{ col: { size: 3 } }}
              name={`${fieldPrefix}_duration_days`}
              label="Days"
              rules={[
                { required: required },
                {
                  validator: (_, value) =>
                    !required && !isActiveField ? Promise.resolve() : validateDuration(value),
                },
              ]}
            >
              <Input type="number" min="0" disabled={hasToggle && !isActiveField} />
            </Form.Field>
          </Col>

          <Col size={helpText ? 6 : 4}>
            <Form.Field
              labelOptions={{ col: { size: 3 } }}
              name={`${fieldPrefix}_duration_hours`}
              label="Hours"
              rules={[
                { required: required },
                {
                  validator: (_, value) =>
                    !required || (required && !isActiveField)
                      ? Promise.resolve()
                      : validateDuration(value),
                },
              ]}
            >
              <Input type="number" min="0" disabled={hasToggle && !isActiveField} />
            </Form.Field>
          </Col>

          {!disableMinutes && (
            <Col size={4}>
              <Form.Field
                labelOptions={{ col: { size: 3 } }}
                name={`${fieldPrefix}_duration_minutes`}
                label="Minutes"
                rules={[
                  { required: required },
                  {
                    validator: (_, value) =>
                      !required || (required && !isActiveField)
                        ? Promise.resolve()
                        : validateDuration(value),
                  },
                ]}
              >
                <Input type="number" min="0" disabled={hasToggle && !isActiveField} />
              </Form.Field>
            </Col>
          )}
        </Row>

        <Space
          style={{ minWidth: '60px' }}
          align="center"
          justify="end"
          size="small"
          className="campaign-form-advanced-tooltip"
        >
          {hasToggle && (
            <Form.Field name={`${fieldPrefix}_is_active`} valuePropName="checked">
              <Switch />
            </Form.Field>
          )}

          {helpText && (
            <Tooltip
              placement="top"
              width={500}
              trigger={helpText ? 'hover' : null}
              title={helpText}
            >
              <Icon type="info" />
            </Tooltip>
          )}
        </Space>
      </Space>
    </Space>
  );
};
